import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  MessageSquare,
  Settings,
  Bell,
  Save,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  FileText,
  RotateCcw,
  Eye,
  Clock,
  ListOrdered,
  Plus,
  Trash2,
  Key,
  Cpu,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_TYPES = [
  { value: "assignment", label: "Assignment Notification", description: "Sent when an action item is assigned" },
  { value: "overdue", label: "Overdue Alert", description: "Sent for overdue action items" },
  { value: "reminder", label: "Daily Reminder", description: "Daily summary of upcoming items" },
  { value: "minutes_ready", label: "Minutes Ready", description: "Sent when meeting minutes are generated" },
];

const TEMPLATE_VARIABLES = {
  assignment: ["{{assignee_name}}", "{{meeting_name}}", "{{description}}", "{{due_date}}"],
  overdue: ["{{items_list}}"],
  reminder: ["{{items_list}}"],
  minutes_ready: ["{{meeting_name}}", "{{meeting_date}}", "{{committee_name}}"],
};

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [activeTab, setActiveTab] = useState("ai");
  
  const [settings, setSettings] = useState({
    email_enabled: false,
    slack_enabled: false,
    teams_enabled: false,
    slack_webhook_url: "",
    teams_webhook_url: "",
    daily_reminder_enabled: false,
    daily_reminder_hour: 9,
  });

  // Email template state
  const [selectedTemplate, setSelectedTemplate] = useState("assignment");
  const [templates, setTemplates] = useState({});
  const [editingTemplate, setEditingTemplate] = useState({ subject: "", html_content: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", html_content: "" });

  // Agenda template state
  const [agendaTemplates, setAgendaTemplates] = useState({ predefined: [], custom: [] });
  const [newAgendaTemplate, setNewAgendaTemplate] = useState({ title: "", description: "", category: "custom" });
  const [savingAgendaTemplate, setSavingAgendaTemplate] = useState(false);

  // AI Settings state
  const [aiSettings, setAiSettings] = useState({
    openai_configured: false,
    openai_key_preview: null,
  });
  const [openaiKey, setOpenaiKey] = useState("");
  const [savingAiSettings, setSavingAiSettings] = useState(false);
  const [testingAi, setTestingAi] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchAgendaTemplates();
    fetchAiSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (templates[selectedTemplate]) {
      setEditingTemplate(templates[selectedTemplate]);
    }
  }, [selectedTemplate, templates]);

  const fetchAiSettings = async () => {
    try {
      const response = await axios.get(`${API}/ai-settings`);
      setAiSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch AI settings");
    }
  };

  const handleSaveOpenAiKey = async () => {
    if (!openaiKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    setSavingAiSettings(true);
    try {
      const response = await axios.put(`${API}/ai-settings`, {
        openai_api_key: openaiKey
      });
      setAiSettings(response.data);
      setOpenaiKey("");
      toast.success("OpenAI API key saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save API key");
    } finally {
      setSavingAiSettings(false);
    }
  };

  const handleRemoveOpenAiKey = async () => {
    setSavingAiSettings(true);
    try {
      await axios.delete(`${API}/ai-settings`);
      setAiSettings({ openai_configured: false, openai_key_preview: null });
      toast.success("OpenAI API key removed");
    } catch (error) {
      toast.error("Failed to remove API key");
    } finally {
      setSavingAiSettings(false);
    }
  };

  const handleTestOpenAi = async () => {
    setTestingAi(true);
    try {
      const response = await axios.post(`${API}/ai-settings/test`);
      if (response.data.success) {
        toast.success("OpenAI connection successful!");
      } else {
        toast.error(response.data.message || "Test failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingAi(false);
    }
  };

  const fetchAgendaTemplates = async () => {
    try {
      const response = await axios.get(`${API}/agenda-templates`);
      setAgendaTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch agenda templates");
    }
  };

  const handleCreateAgendaTemplate = async () => {
    if (!newAgendaTemplate.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setSavingAgendaTemplate(true);
    try {
      await axios.post(`${API}/agenda-templates`, newAgendaTemplate);
      toast.success("Agenda template created");
      setNewAgendaTemplate({ title: "", description: "", category: "custom" });
      fetchAgendaTemplates();
    } catch (error) {
      toast.error("Failed to create agenda template");
    } finally {
      setSavingAgendaTemplate(false);
    }
  };

  const handleDeleteAgendaTemplate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/agenda-templates/${id}`);
      toast.success("Template deleted");
      fetchAgendaTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/entity/notification-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/entity/email-templates`);
      setTemplates(response.data);
      if (response.data[selectedTemplate]) {
        setEditingTemplate(response.data[selectedTemplate]);
      }
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/entity/notification-settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (channel) => {
    setTesting(channel);
    try {
      const response = await axios.post(`${API}/entity/test-notification?channel=${channel}`);
      if (response.data.success) {
        toast.success(`Test ${channel} notification sent!`);
      } else {
        toast.error(`Test ${channel} notification failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send test ${channel} notification`);
    } finally {
      setTesting("");
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/entity/email-templates/${selectedTemplate}`, editingTemplate);
      toast.success("Template saved successfully");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    try {
      const response = await axios.post(`${API}/entity/email-templates/${selectedTemplate}/reset`);
      setEditingTemplate(response.data.template);
      setTemplates({ ...templates, [selectedTemplate]: response.data.template });
      toast.success("Template reset to default");
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const handlePreviewTemplate = async () => {
    try {
      const response = await axios.post(`${API}/entity/email-templates/preview?template_type=${selectedTemplate}`);
      setPreviewContent(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error("Failed to preview template");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-stone-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary-700" />
                <h1 className="font-heading text-lg font-semibold text-stone-900">
                  Settings
                </h1>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary-700 hover:bg-primary-700/90"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              AI Configuration
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Email Templates
            </TabsTrigger>
            <TabsTrigger value="agenda" className="flex items-center gap-2">
              <ListOrdered className="w-4 h-4" />
              Agenda Templates
            </TabsTrigger>
            <TabsTrigger value="scheduler" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Scheduled Jobs
            </TabsTrigger>
          </TabsList>

          {/* AI Configuration Tab */}
          <TabsContent value="ai" className="space-y-6">
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Key className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">OpenAI API Key</h3>
                    <p className="text-sm text-stone-500">
                      Configure your own OpenAI API key for transcription and minutes generation
                    </p>
                  </div>
                </div>
                {aiSettings.openai_configured && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Configured</span>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {aiSettings.openai_configured ? (
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                    <div>
                      <p className="text-sm text-stone-600">Current API Key</p>
                      <p className="font-mono text-stone-800">sk-...{aiSettings.openai_key_preview}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleTestOpenAi}
                        disabled={testingAi}
                      >
                        {testingAi ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleRemoveOpenAiKey}
                        disabled={savingAiSettings}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>No API key configured.</strong> Add your OpenAI API key to enable audio transcription and AI-powered minutes generation.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="openai-key">OpenAI API Key</Label>
                      <div className="flex gap-2">
                        <Input
                          id="openai-key"
                          type="password"
                          placeholder="sk-..."
                          value={openaiKey}
                          onChange={(e) => setOpenaiKey(e.target.value)}
                          className="font-mono"
                        />
                        <Button
                          onClick={handleSaveOpenAiKey}
                          disabled={savingAiSettings || !openaiKey.trim()}
                        >
                          {savingAiSettings ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Key
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t border-stone-200 pt-4 mt-4">
                  <h4 className="font-medium text-stone-900 mb-2">How to get your OpenAI API Key:</h4>
                  <ol className="list-decimal list-inside text-sm text-stone-600 space-y-1">
                    <li>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">platform.openai.com/api-keys <ExternalLink className="w-3 h-3" /></a></li>
                    <li>Create a new secret key</li>
                    <li>Copy and paste it here</li>
                  </ol>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Estimated costs:</strong><br />
                      • Transcription (Whisper): ~$0.006 per minute of audio<br />
                      • Minutes generation (GPT-4o): ~$0.01-0.05 per meeting
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            {/* Email Notifications */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <Mail className="w-5 h-5 text-primary-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-stone-900">
                      Email Notifications
                    </h3>
                    <p className="text-sm text-stone-500">
                      Send email notifications via SendGrid
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.email_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, email_enabled: checked })
                  }
                  data-testid="email-toggle"
                />
              </div>

              {settings.email_enabled && (
                <div className="mt-4 pt-4 border-t border-stone-100 space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>Note:</strong> Ensure SENDGRID_API_KEY is configured in your backend environment.
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTest("email")}
                    disabled={testing === "email"}
                    data-testid="test-email-btn"
                  >
                    {testing === "email" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test Email
                  </Button>
                </div>
              )}
            </div>

            {/* Slack Integration */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-stone-900">
                      Slack Integration
                    </h3>
                    <p className="text-sm text-stone-500">
                      Post notifications to a Slack channel
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.slack_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, slack_enabled: checked })
                  }
                  data-testid="slack-toggle"
                />
              </div>

              {settings.slack_enabled && (
                <div className="mt-4 pt-4 border-t border-stone-100 space-y-4">
                  <div>
                    <Label>Slack Webhook URL</Label>
                    <Input
                      type="url"
                      value={settings.slack_webhook_url}
                      onChange={(e) =>
                        setSettings({ ...settings, slack_webhook_url: e.target.value })
                      }
                      placeholder="https://hooks.slack.com/services/..."
                      className="mt-1"
                      data-testid="slack-webhook-input"
                    />
                    <p className="text-xs text-stone-400 mt-1">
                      Create an Incoming Webhook in your Slack workspace settings
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTest("slack")}
                    disabled={testing === "slack" || !settings.slack_webhook_url}
                    data-testid="test-slack-btn"
                  >
                    {testing === "slack" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test Message
                  </Button>
                </div>
              )}
            </div>

            {/* Microsoft Teams Integration */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Bell className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-stone-900">
                      Microsoft Teams Integration
                    </h3>
                    <p className="text-sm text-stone-500">
                      Post notifications to a Teams channel
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.teams_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, teams_enabled: checked })
                  }
                  data-testid="teams-toggle"
                />
              </div>

              {settings.teams_enabled && (
                <div className="mt-4 pt-4 border-t border-stone-100 space-y-4">
                  <div>
                    <Label>Teams Webhook URL</Label>
                    <Input
                      type="url"
                      value={settings.teams_webhook_url}
                      onChange={(e) =>
                        setSettings({ ...settings, teams_webhook_url: e.target.value })
                      }
                      placeholder="https://outlook.office.com/webhook/..."
                      className="mt-1"
                      data-testid="teams-webhook-input"
                    />
                    <p className="text-xs text-stone-400 mt-1">
                      Create an Incoming Webhook connector in your Teams channel
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTest("teams")}
                    disabled={testing === "teams" || !settings.teams_webhook_url}
                    data-testid="test-teams-btn"
                  >
                    {testing === "teams" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test Message
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-lg font-medium text-stone-900">
                    Email Templates
                  </h3>
                  <p className="text-sm text-stone-500">
                    Customize notification email templates
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviewTemplate}
                    data-testid="preview-template-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetTemplate}
                    data-testid="reset-template-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate}
                    className="bg-primary-700 hover:bg-primary-700/90"
                    data-testid="save-template-btn"
                  >
                    {savingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Template
                  </Button>
                </div>
              </div>

              {/* Template Selector */}
              <div className="mb-6">
                <Label>Select Template Type</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger className="mt-1" data-testid="template-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex flex-col">
                          <span>{type.label}</span>
                          <span className="text-xs text-stone-400">{type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Available Variables */}
              <div className="mb-6 p-3 bg-stone-50 rounded-lg">
                <p className="text-sm font-medium text-stone-700 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {(TEMPLATE_VARIABLES[selectedTemplate] || []).map((variable) => (
                    <code
                      key={variable}
                      className="px-2 py-1 bg-white border border-stone-200 rounded text-xs text-primary-700 cursor-pointer hover:bg-primary-50"
                      onClick={() => {
                        navigator.clipboard.writeText(variable);
                        toast.success("Variable copied to clipboard");
                      }}
                    >
                      {variable}
                    </code>
                  ))}
                </div>
                <p className="text-xs text-stone-400 mt-2">Click to copy. These will be replaced with actual values when sending.</p>
              </div>

              {/* Subject */}
              <div className="mb-4">
                <Label>Email Subject</Label>
                <Input
                  value={editingTemplate.subject || ""}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, subject: e.target.value })
                  }
                  placeholder="Email subject line..."
                  className="mt-1"
                  data-testid="template-subject-input"
                />
              </div>

              {/* HTML Content */}
              <div>
                <Label>Email Body (HTML)</Label>
                <Textarea
                  value={editingTemplate.html_content || ""}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, html_content: e.target.value })
                  }
                  placeholder="<html>...</html>"
                  className="mt-1 font-mono text-sm min-h-[300px]"
                  data-testid="template-content-input"
                />
                <p className="text-xs text-stone-400 mt-1">
                  Use HTML for formatting. Variables will be replaced with actual values.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Agenda Templates Tab */}
          <TabsContent value="agenda" className="space-y-6">
            {/* Create New Template */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
                Create Custom Agenda Template
              </h3>
              <p className="text-sm text-stone-500 mb-4">
                Create your own agenda items that will appear in the Quick Insert menu.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={newAgendaTemplate.title}
                    onChange={(e) => setNewAgendaTemplate({ ...newAgendaTemplate, title: e.target.value })}
                    placeholder="e.g., Safety Report"
                    className="mt-1"
                    data-testid="new-agenda-template-title"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select
                    value={newAgendaTemplate.category}
                    onValueChange={(value) => setNewAgendaTemplate({ ...newAgendaTemplate, category: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="opening">Opening</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="reports">Reports</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="closing">Closing</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Label>Description (optional)</Label>
                <Textarea
                  value={newAgendaTemplate.description}
                  onChange={(e) => setNewAgendaTemplate({ ...newAgendaTemplate, description: e.target.value })}
                  placeholder="Brief description of this agenda item..."
                  className="mt-1 min-h-[80px]"
                  data-testid="new-agenda-template-desc"
                />
              </div>
              <Button
                onClick={handleCreateAgendaTemplate}
                disabled={savingAgendaTemplate || !newAgendaTemplate.title.trim()}
                className="mt-4 bg-primary-700 hover:bg-primary-700/90"
                data-testid="create-agenda-template-btn"
              >
                {savingAgendaTemplate ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Template
              </Button>
            </div>

            {/* Your Custom Templates */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
                Your Custom Templates
              </h3>
              {agendaTemplates.custom?.length > 0 ? (
                <div className="space-y-3">
                  {agendaTemplates.custom.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-100"
                    >
                      <div>
                        <div className="font-medium text-stone-900">{template.title}</div>
                        {template.description && (
                          <div className="text-sm text-stone-500">{template.description}</div>
                        )}
                        <div className="text-xs text-stone-400 mt-1">
                          Category: {template.category}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAgendaTemplate(template.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-agenda-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <ListOrdered className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                  <p className="text-sm">No custom templates yet.</p>
                  <p className="text-xs text-stone-400 mt-1">
                    Create templates above to quickly insert common agenda items.
                  </p>
                </div>
              )}
            </div>

            {/* Predefined Templates Reference */}
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-6">
              <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
                System Templates (Read-only)
              </h3>
              <p className="text-sm text-stone-500 mb-4">
                These common agenda items are available to all users in Quick Insert.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {agendaTemplates.predefined?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    {item.title}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Scheduled Jobs Tab */}
          <TabsContent value="scheduler" className="space-y-6">
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Clock className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-stone-900">
                      Daily Reminder Notifications
                    </h3>
                    <p className="text-sm text-stone-500">
                      Send automated daily reminders for upcoming and overdue action items
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.daily_reminder_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, daily_reminder_enabled: checked })
                  }
                  data-testid="daily-reminder-toggle"
                />
              </div>

              {settings.daily_reminder_enabled && (
                <div className="mt-4 pt-4 border-t border-stone-100 space-y-4">
                  <div>
                    <Label>Reminder Time (24-hour format, UTC)</Label>
                    <Select
                      value={String(settings.daily_reminder_hour)}
                      onValueChange={(value) =>
                        setSettings({ ...settings, daily_reminder_hour: parseInt(value) })
                      }
                    >
                      <SelectTrigger className="mt-1 w-40" data-testid="reminder-hour-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {String(i).padStart(2, "0")}:00 UTC
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-400 mt-1">
                      Daily reminders will be sent at this time to users with due/overdue items
                    </p>
                  </div>

                  <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <h4 className="font-medium text-primary-900 mb-2">What gets sent:</h4>
                    <ul className="text-sm text-primary-700 space-y-1">
                      <li>• <strong>Email:</strong> Individual emails to each assignee with their action items</li>
                      <li>• <strong>Slack/Teams:</strong> Daily summary to the configured channel</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h4 className="font-medium text-primary-900 mb-2">How Scheduled Jobs Work</h4>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Jobs run automatically in the background</li>
                <li>• Notifications are only sent to assignees with items due within 7 days or overdue</li>
                <li>• Make sure at least one notification channel (Email/Slack/Teams) is enabled</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-stone-500">Subject</Label>
              <p className="font-medium text-stone-900">{previewContent.subject}</p>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Body</Label>
              <div
                className="border border-stone-200 rounded-lg p-4 bg-white mt-1"
                dangerouslySetInnerHTML={{ __html: previewContent.html_content }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
